import {QuestionDifficulty} from '@domain/pom'

import {MONTH_COUNT} from '../../consts'
import {
  DifficultyFilter,
  MonthlyHierarchyAnalysisDataPoint,
  ValueDisplayMode,
  HierarchyEntry
} from '../../types'

import {getAnswered, getTotal} from './difficultyScore.utils'

export type DifficultyTotals = {
  basic: {answered: number; total: number}
  advanced: {answered: number; total: number}
  excellence: {answered: number; total: number}
}

type TooltipFormatterParams = {
  basicLabel: string
  advancedLabel: string
  excellenceLabel: string
  perNodeDifficulty: Map<string, Array<DifficultyTotals | null>>
  getTitle: (dataIndex: number) => string
  difficultyFilter?: DifficultyFilter
  valueDisplayMode?: ValueDisplayMode
  colorMap?: Map<string, string>
}

export const buildPerNodeMonthlyDifficulty = (
  hierarchyEntries: HierarchyEntry[],
  monthlyData: MonthlyHierarchyAnalysisDataPoint[],
  year?: number
): Map<string, Array<DifficultyTotals | null>> => {
  const result = new Map<string, Array<DifficultyTotals | null>>()

  for (const entry of hierarchyEntries) {
    const entryData = monthlyData.filter((dp) => dp.hierarchyId === entry.id)

    const toDifficultyTotals = (point: MonthlyHierarchyAnalysisDataPoint): DifficultyTotals => ({
      basic: {
        answered: getAnswered(point.difficultyScores?.basic),
        total: getTotal(point.difficultyScores?.basic)
      },
      advanced: {
        answered: getAnswered(point.difficultyScores?.advanced),
        total: getTotal(point.difficultyScores?.advanced)
      },
      excellence: {
        answered: getAnswered(point.difficultyScores?.excellence),
        total: getTotal(point.difficultyScores?.excellence)
      }
    })

    let nodeData: Array<DifficultyTotals | null>

    if (year !== undefined) {
      // 13-item array: index 0 = Dec of year-1, indices 1-12 = Jan-Dec of year
      const dataByMonthStr = new Map(entryData.map((dp) => [dp.month.substring(0, 7), dp]))

      const prevDecPoint = dataByMonthStr.get(`${year - 1}-12`)
      const prevDecDiff = prevDecPoint?.difficultyScores ? toDifficultyTotals(prevDecPoint) : null

      const regularData = Array.from({length: MONTH_COUNT}, (_, i) => {
        const monthStr = `${year}-${String(i + 1).padStart(2, '0')}`
        const point = dataByMonthStr.get(monthStr)
        return point?.difficultyScores ? toDifficultyTotals(point) : null
      })

      nodeData = [prevDecDiff, ...regularData]
    } else {
      const dataByMonth = new Map(entryData.map((dp) => [new Date(dp.month).getMonth(), dp]))
      nodeData = Array.from({length: MONTH_COUNT}, (_, monthIndex) => {
        const point = dataByMonth.get(monthIndex)
        if (point?.difficultyScores) return toDifficultyTotals(point)
        return null
      })
    }

    result.set(entry.name, nodeData)
  }

  return result
}

export const createTooltipFormatter =
  ({
    basicLabel,
    advancedLabel,
    excellenceLabel,
    perNodeDifficulty,
    getTitle,
    difficultyFilter,
    valueDisplayMode,
    colorMap
  }: TooltipFormatterParams) =>
  (params: {dataIndex?: number; seriesName?: string} | null | undefined): string => {
    if (
      !params ||
      params.dataIndex === null ||
      params.dataIndex === undefined ||
      !params.seriesName
    )
      return ''

    const dataIndex = params.dataIndex
    const seriesName = params.seriesName
    const title = getTitle(dataIndex)

    const color = colorMap?.get(seriesName)
    const dot = color
      ? `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${color};margin-right:6px;flex-shrink:0;"></span>`
      : ''

    const nodeData = perNodeDifficulty.get(seriesName)
    const diffData = nodeData?.[dataIndex]
    if (!diffData)
      return `<div style="display:flex;align-items:center;">${dot}<p class="font-semibold text-16px leading-24px">${seriesName}</p></div><br/>Count: ${title}`

    const isPercentage = valueDisplayMode === 'percentage'
    const formatValue = (answered: number, total: number) => {
      if (isPercentage && total > 0) {
        const pct = Math.round((answered / total) * 100)
        return `<span class="font-semibold">${pct}%</span>`
      }
      const pct = total > 0 ? Math.round((answered / total) * 100) : 0
      return `<span class="font-semibold">${answered}</span>/${total} <span style="opacity:0.75">(${pct}%)</span>`
    }

    const difficultyLines: string[] = []
    const filter = difficultyFilter ?? 'all'
    if (filter === 'all' || filter === QuestionDifficulty.Basic) {
      difficultyLines.push(
        `<p class="font-normal text-16px leading-24px">${basicLabel} ${formatValue(diffData.basic.answered, diffData.basic.total)}</p>`
      )
    }
    if (filter === 'all' || filter === QuestionDifficulty.Advanced) {
      difficultyLines.push(
        `<p class="font-normal text-16px leading-24px">${advancedLabel} ${formatValue(diffData.advanced.answered, diffData.advanced.total)}</p>`
      )
    }
    if (filter === 'all' || filter === QuestionDifficulty.Excellence) {
      difficultyLines.push(
        `<p class="font-normal text-16px leading-24px">${excellenceLabel} ${formatValue(diffData.excellence.answered, diffData.excellence.total)}</p>`
      )
    }

    return [
      `<div style="display:flex;align-items:center;margin-bottom:4px;">${dot}<p class="font-semibold text-16px leading-24px">${seriesName}</p></div>`,
      `<p class="font-normal text-14px leading-20px mb-2">Count: ${title}</p>`,
      ...difficultyLines
    ].join('')
  }
